import { useEffect, useRef } from "react";
import { Client } from "@stomp/stompjs";
import { config } from "../config";
import { getRabbitMqConfig } from "../api/settings";
import logger from "../lib/logger";
import type { StreamSlot } from "../types/stream";

export interface RBActivity {
  behavior: string;
  users?: string[];
  conv?: { id: number; type: string } | null;
}

export interface RBTargetUser {
  userId: string;
  lat?: number | null;
  lng?: number | null;
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  avatar?: string | null;
  online?: boolean | null;
  talkAbout?: string | null;
  ringColor?: string | null;
  streetChatId?: number | null;
  convId?: number | null;
  tc1Id?: number | null;
  tc2Id?: number | null;
}

export interface RBMetadata {
  targetUsers?: RBTargetUser[];
  activities?: RBActivity[];
  slot?: StreamSlot;
  /** room-stc cast events of a slot with a queue (spec 2026-10-16 §3.5): the Queue object; parse with parseQueue. */
  queue?: unknown;
  /** room-stc cast events: server epoch ms when the event was published. */
  serverTime?: number;
  /** GC wall payload (room-stc WALL_* events); shape depends on the behavior. */
  wall?: unknown;
  /** room-stc CAST_KEEPALIVE_* events (spec 2026-10-19 §3.2); parse with castKeepalive. */
  keepalive?: unknown;
}

export interface RBEvent {
  action: "STC_EVENT" | "TC_EVENT" | "GC_EVENT";
  metadata: RBMetadata;
}

// ─── Module-level bus ────────────────────────────────────────────────────────
// One STOMP client per queue name. All useRabbitMQ callers share it.
// Each caller registers a handler in the Set; every message fans out to all.

interface QueueEntry {
  client: Client;
  handlers: Set<(event: RBEvent) => void>;
}

const _queues = new Map<string, QueueEntry>();

const TICKET_RETRY_MIN_MS = 1_000;
const TICKET_RETRY_MAX_MS = 30_000;

/**
 * stompjs `beforeConnect`: runs before every connect attempt, auto-reconnects included. The broker login is a
 * 10-minute ticket (spec 2026-10-31 §1), so it is never cached: each attempt fetches a fresh one, and a refused login
 * (expired ticket, ACCESS_REFUSED) is fixed by the next attempt. A failed fetch is retried with backoff while the
 * client stays active (stompjs schedules no reconnect when beforeConnect throws). Never log the ticket.
 */
async function _fetchTicket(client: Client, queueName: string): Promise<void> {
  let delay = TICKET_RETRY_MIN_MS;
  for (;;) {
    try {
      const { username, password } = await getRabbitMqConfig();
      client.connectHeaders = { login: username, passcode: password };
      logger.debug("[RabbitMQBus] Broker ticket fetched for queue:", queueName);
      return;
    } catch (err) {
      if (!client.active) return;
      logger.warn(
        `[RabbitMQBus] Broker ticket fetch failed, retrying in ${delay} ms:`,
        err,
      );
      await new Promise((r) => setTimeout(r, delay));
      if (!client.active) return;
      delay = Math.min(delay * 2, TICKET_RETRY_MAX_MS);
    }
  }
}

// ─── Channel lifecycle events ───────────────────────────────────────────────
// "reconnected": the STOMP connection came back after a drop; events sent in
// between were missed, so screens re-fetch their state.
// "queueMissing": the broker no longer has our queue (expired or deleted);
// the channel name must be fetched again from /api/user/channel.
type ChannelEvent = "reconnected" | "queueMissing";
const _channelListeners = new Set<(e: ChannelEvent) => void>();

export function onRabbitChannelEvent(
  listener: (e: ChannelEvent) => void,
): () => void {
  _channelListeners.add(listener);
  return () => _channelListeners.delete(listener);
}

function _emit(e: ChannelEvent) {
  logger.info("[RabbitMQBus] channel event:", e);
  _channelListeners.forEach((l) => l(e));
}

/**
 * Someone else learned that the device queue is gone (e.g. `PUT /room/api/user/channel/area` answered 404): run the
 * same queueMissing path as a broker NOT_FOUND, so the channel request re-creates the queue.
 */
export function notifyQueueMissing(): void {
  _emit("queueMissing");
}

function _buildClient(queueName: string): Client {
  let connectedOnce = false;
  const client = new Client({
    brokerURL: config.rabbitMqWsUrl,
    beforeConnect: (c) => _fetchTicket(c, queueName),
    onConnect: (frame) => {
      if (connectedOnce) _emit("reconnected");
      connectedOnce = true;
      logger.info(
        "[RabbitMQBus] Connected, frame:",
        frame.command,
        "— subscribing to /amq/queue/" + queueName,
      );
      client.subscribe(`/amq/queue/${queueName}`, (frame) => {
        logger.debug(
          "[RabbitMQBus] Raw message on /amq/queue/" + queueName + ":",
          frame.body,
        );
        let event: RBEvent;
        try {
          event = JSON.parse(frame.body) as RBEvent;
        } catch {
          logger.warn("[RabbitMQBus] Malformed message:", frame.body);
          return;
        }
        const entry = _queues.get(queueName);
        if (!entry) return;
        logger.debug(
          "[RabbitMQBus] Dispatching",
          event.action,
          "behaviors:",
          event.metadata.activities?.map((a) => a.behavior),
          "→",
          entry.handlers.size,
          "handler(s)",
        );
        entry.handlers.forEach((h) => h(event));
      });
    },
    onStompError: (frame) => {
      logger.error(
        "[RabbitMQBus] STOMP error:",
        frame.headers["message"],
        "body:",
        frame.body,
      );
      // Emit queueMissing for two broker error messages:
      // 1. "NOT_FOUND - no queue" (when queue does not exist)
      // 2. "Server cancelled subscription" (when queue is deleted while connected)
      const message = frame.headers["message"] ?? "";
      const body = frame.body ?? "";
      if (
        /NOT_FOUND - no queue/i.test(body) ||
        /Server cancelled subscription/i.test(message) ||
        /Server cancelled subscription/i.test(body)
      ) {
        _emit("queueMissing");
        // The broker keeps the connection open after cancelling the
        // subscription, so nothing would re-subscribe. Drop the socket; stompjs
        // reconnects after reconnectDelay, re-running onConnect (re-SUBSCRIBE +
        // "reconnected"). If the queue is still missing the cycle repeats until
        // the channel request has re-created it.
        client.forceDisconnect();
      }
    },
    onWebSocketError: (evt) =>
      logger.error("[RabbitMQBus] WebSocket error:", evt),
    onWebSocketClose: (evt) =>
      logger.warn(
        "[RabbitMQBus] WebSocket closed — code:",
        evt.code,
        "reason:",
        evt.reason,
      ),
    onDisconnect: () =>
      logger.info("[RabbitMQBus] Disconnected from queue:", queueName),
    reconnectDelay: 5000,
  });
  return client;
}

function _busSubscribe(
  queueName: string,
  handler: (event: RBEvent) => void,
  label: string,
): void {
  let entry = _queues.get(queueName);
  if (!entry) {
    logger.debug(
      "[RabbitMQBus] First subscriber for queue:",
      queueName,
      "— connecting",
    );
    const client = _buildClient(queueName);
    entry = { client, handlers: new Set() };
    _queues.set(queueName, entry);
    client.activate();
  }
  entry.handlers.add(handler);
  logger.debug(
    `[RabbitMQBus:${label}] Handler registered — queue: ${queueName}, total: ${entry.handlers.size}`,
  );
}

function _busUnsubscribe(
  queueName: string,
  handler: (event: RBEvent) => void,
  label: string,
): void {
  const entry = _queues.get(queueName);
  if (!entry) return;
  entry.handlers.delete(handler);
  logger.debug(
    `[RabbitMQBus:${label}] Handler removed — queue: ${queueName}, remaining: ${entry.handlers.size}`,
  );
  if (entry.handlers.size === 0) {
    logger.info(
      "[RabbitMQBus] No handlers left — deactivating client for queue:",
      queueName,
    );
    void entry.client.deactivate();
    _queues.delete(queueName);
  }
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useRabbitMQ(
  queueName: string | null,
  onMessage: (event: RBEvent) => void,
  label = "unknown",
) {
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    if (!queueName) {
      logger.debug(`[RabbitMQBus:${label}] Waiting — queueName is null`);
      return;
    }

    // Stable proxy: always calls the latest onMessage without re-registering
    const handler = (event: RBEvent) => {
      logger.debug(
        `[RabbitMQBus:${label}] Received`,
        event.action,
        "behaviors:",
        event.metadata.activities?.map((a) => a.behavior),
      );
      onMessageRef.current(event);
    };

    _busSubscribe(queueName, handler, label);
    return () => _busUnsubscribe(queueName, handler, label);
  }, [queueName, label]);
}
